import type { SliceMetric } from '@death-star/shared';

/**
 * Shared formatting + metric-classification helpers for the slicing dashboard.
 *
 * These were previously hand-rolled (and subtly divergent) in four separate
 * components — SliceTile, SliceBarChart, SliceTrendChart, and MetricHeader.
 * The divergence caused the "25127%" bug: MetricHeader's inline time-metric
 * check omitted `council_wall_clock`, so a p95 duration in seconds (~251) was
 * run through the percentage formatter. Centralizing here keeps a single source
 * of truth.
 */

/** Metrics whose values are durations measured in seconds, not 0..1 rates. */
const TIME_METRICS = new Set<string>(['ttm', 'ttfr', 'council_wall_clock']);

/**
 * Metrics whose value is an unbounded ratio — neither a 0..1 rate nor a
 * duration. `findings_per_run` is findings ÷ council runs: council-wide it sits
 * near 0.12, but a single size bucket can exceed 1. Running it through PCT
 * would render 2.5 findings per run as "250%", and the bar chart's [0, 1]
 * domain would clip the bar. Classifying it once here rather than special-
 * casing it at each call site is the same lesson the "25127%" bug taught.
 */
const RATIO_METRICS = new Set<string>(['findings_per_run']);

/**
 * Metrics whose sample unit is a FINDING rather than a PR or a council run.
 * Mislabelling these is the same class of defect as mis-formatting them: a
 * correct number under a label that misdescribes what was counted.
 */
const FINDING_SAMPLE_METRICS = new Set<string>(['substantive_share', 'severity_mix']);

/** Metrics that have no baseline comparison (current-only bars). */
const NO_BASELINE_METRICS = new Set<string>([
  'auto_approval', 'override_rate',
  // The finding-quality metrics never carry a baseline, so without this the
  // chart renders an empty grey "baseline" series and a two-item legend.
  'findings_per_run', 'substantive_share', 'severity_mix',
]);

/**
 * Metrics where a *lower* value is the improvement. Used to color the
 * "vs baseline" delta correctly. auto_approval is the only higher-is-better
 * metric; every other slicing metric (time-to-merge, revert/override
 * rates, council wall-clock) improves as it goes down.
 */
const LOWER_IS_BETTER = new Set<string>([
  'ttm',
  'ttfr',
  'council_wall_clock',
  'revert_rate',
  'override_rate',
]);

export function isTimeMetric(metric: string): boolean {
  return TIME_METRICS.has(metric);
}

export function isRatioMetric(metric: string): boolean {
  return RATIO_METRICS.has(metric);
}

/** Label for the sample-count tile, matching what the denominator counts. */
export function sampleLabel(metric: string): string {
  if (FINDING_SAMPLE_METRICS.has(metric)) return 'Findings';
  if (isRatioMetric(metric)) return 'Sample (runs)';
  return isTimeMetric(metric) ? 'Samples' : 'Sample (PRs)';
}

export function isNoBaselineMetric(metric: string): boolean {
  return NO_BASELINE_METRICS.has(metric);
}

/**
 * Direction of improvement for a metric.
 * 'lower' = a decrease is good, 'higher' = an increase is good.
 */
export function metricDirection(metric: string): 'lower' | 'higher' {
  return LOWER_IS_BETTER.has(metric) ? 'lower' : 'higher';
}

/** Render a 0..1 rate as a whole-number percentage. */
export const PCT = (v: number) => `${(v * 100).toFixed(0)}%`;

/** Render a duration in seconds as minutes (<= 1h) or hours. */
export const SECS = (v: number) => (Math.abs(v) > 3600 ? `${(v / 3600).toFixed(1)} h` : `${Math.round(v / 60)} m`);

/**
 * Render an unbounded ratio. Two decimals, because findings-per-run is
 * council-wide around 0.12 and a whole-number format would flatten every
 * bucket to "0".
 */
export const RATIO = (v: number) => v.toFixed(2);

/**
 * Pick the value formatter for a given metric. Time metrics get the
 * minutes/hours duration formatter; everything else is a 0..1 rate rendered
 * as a percentage. An explicit override always wins.
 */
export function pickFormatter(metric: SliceMetric, override?: (v: number) => string): (v: number) => string {
  if (override) return override;
  if (isTimeMetric(metric)) return SECS;
  if (isRatioMetric(metric)) return RATIO;
  return PCT;
}

/** Format a single metric value using the metric's natural formatter. */
export function formatValue(metric: SliceMetric, value: number): string {
  return pickFormatter(metric)(value);
}
